/**
 * Utilities for parsing data intervals and generating time separators.
 */

import { DAY, HOUR, MINUTE, MONTH, WEEK, YEAR } from "./constants";
import type {
  DataInterval,
  TickerChartDateRange,
  TimeSeparator,
  TimeSeparatorType,
} from "~types/types";
import { getYTDInDays } from "~utils/dateTimeUtils";
import { INTERVALS_BY_DATE_RANGE } from "~constants/ticker-chart";

/**
 * Mapping of interval strings to milliseconds.
 */
export const INTERVAL_MS: Record<DataInterval, number> = {
  "1m": MINUTE,
  "5m": 5 * MINUTE,
  "15m": 15 * MINUTE,
  "30m": 30 * MINUTE,
  "1h": HOUR,
  "2h": 2 * HOUR,
  "4h": 4 * HOUR,
  "1d": DAY,
  "1w": WEEK,
  "1M": MONTH,
  "1y": YEAR,
};

// ==================== Interval Functions ====================

/**
 * Convert DataInterval to milliseconds.
 */
export function intervalToMs(interval: DataInterval): number {
  return INTERVAL_MS[interval];
}

/**
 * Get gap threshold in milliseconds.
 * A gap larger than this threshold is considered a "large gap".
 */
export function getGapThreshold(
  interval: DataInterval,
  multiplier = 3
): number {
  return intervalToMs(interval) * multiplier;
}

// ==================== Time Separator Functions ====================

/**
 * Determine appropriate separator type based on visible time range.
 */
export function getTimeSeparatorType(rangeMs: number): TimeSeparatorType {
  if (rangeMs < DAY) return "hour";
  if (rangeMs < WEEK) return "day";
  if (rangeMs < MONTH) return "week";
  if (rangeMs < YEAR) return "month";

  return "year";
}

/**
 * Align a date to the start of the given separator type.
 */
function alignToSeparatorStart(date: Date, type: TimeSeparatorType): void {
  switch (type) {
    case "hour":
      date.setMinutes(0, 0, 0);
      break;
    case "day":
      date.setHours(0, 0, 0, 0);
      break;
    case "week":
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - date.getDay());
      break;
    case "month":
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
      break;
    case "year":
      date.setMonth(0, 1);
      date.setHours(0, 0, 0, 0);
      break;
  }
}

/**
 * Advance date to next separator boundary.
 */
function advanceToNextSeparator(date: Date, type: TimeSeparatorType): void {
  switch (type) {
    case "hour":
      date.setHours(date.getHours() + 1);
      break;
    case "day":
      date.setDate(date.getDate() + 1);
      break;
    case "week":
      date.setDate(date.getDate() + 7);
      break;
    case "month":
      date.setMonth(date.getMonth() + 1);
      break;
    case "year":
      date.setFullYear(date.getFullYear() + 1);
      break;
  }
}

/**
 * Format separator label based on type.
 */
function formatSeparatorLabel(date: Date, type: TimeSeparatorType): string {
  switch (type) {
    case "hour":
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    case "day":
      return date.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
      });
    case "week":
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    case "month":
      return date.toLocaleDateString(undefined, {
        month: "short",
        year: date.getMonth() === 0 ? "numeric" : undefined,
      });
    case "year":
      return date.getFullYear().toString();
  }

  throw new Error(`Unsupported format type: ${type}`);
}

/**
 * Generate time separator timestamps within a range.
 */
export function generateTimeSeparators(
  start: number,
  end: number,
  type?: TimeSeparatorType
): TimeSeparator[] {
  const rangeMs = end - start;
  const separatorType = type ?? getTimeSeparatorType(rangeMs);
  const separators: TimeSeparator[] = [];

  const date = new Date(start);

  alignToSeparatorStart(date, separatorType);

  // If alignment moved us before start, advance once
  if (date.getTime() < start) {
    advanceToNextSeparator(date, separatorType);
  }

  while (date.getTime() <= end) {
    const timestamp = date.getTime();

    if (timestamp >= start) {
      separators.push({
        timestamp,
        type: separatorType,
        label: formatSeparatorLabel(date, separatorType),
      });
    }

    advanceToNextSeparator(date, separatorType);
  }

  return separators;
}

/**
 * Check if a timestamp falls within any of the provided gaps.
 */
export function isTimestampInGap(
  timestamp: number,
  gaps: Array<{ start: number; end: number; isLarge?: boolean }>,
  largeOnly = false
): boolean {
  for (const gap of gaps) {
    if (largeOnly && !gap.isLarge) continue;

    if (timestamp > gap.start && timestamp < gap.end) {
      return true;
    }
  }

  return false;
}

export function getAvailableIntervals(
  dateRange: TickerChartDateRange
): DataInterval[] {
  if (dateRange === "YTD") {
    const ytdDays = getYTDInDays();

    if (ytdDays <= 3) {
      return INTERVALS_BY_DATE_RANGE["1D"];
    }

    if (ytdDays <= 31) {
      return INTERVALS_BY_DATE_RANGE["1M"];
    }

    return INTERVALS_BY_DATE_RANGE.YTD;
  }

  return INTERVALS_BY_DATE_RANGE[dateRange];
}

export function getDefaultInterval(
  dateRange: TickerChartDateRange
): DataInterval {
  switch (dateRange) {
    case "1H":
      return "1m";
    case "1D":
      return "5m";
    case "1M":
      return "1h";
    case "6M":
      return "1d";
    case "YTD": {
      const ytdDays = getYTDInDays();

      if (ytdDays <= 3) {
        return "5m";
      }

      return ytdDays <= 31 ? "1h" : "1d";
    }
    case "1Y":
      return "1d";
    case "5Y":
      return "1M";
    default:
      return "1d";
  }
}
